import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Stack } from '@mui/system';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPayerCateGroup } from 'src/layouts/components/utils-method';
import {
  getPayerHistoryAction,
  getPayerOptionsAction,
  resetGetPayerHistoryAction,
  resetGetPayerOptionsAction,
} from 'src/redux/actions/patientPayerActions';

type PayerHistoryChip = { label: string; payerType: string };
type PayerHistoryRow = { label: string; from?: string; to: string; isAdded?: boolean };
type PayerHistorySection = { heading?: string; rows: PayerHistoryRow[] };
// The header line is a sequence of chip/text segments rather than one flat string, so a
// chip can sit directly before its own payer name even when two payers are named in one line
// (e.g. "Ended [chip] Name A and added [chip] Name B").
type DescriptionPart = { type: 'text'; value: string } | { type: 'chip'; chip: PayerHistoryChip };
type PayerHistoryEntry = {
  date: string;
  time: string;
  author: string;
  isImported: boolean;
  action: string;
  isDeleted: boolean;
  descriptionParts: DescriptionPart[];
  note?: string;
  sections: PayerHistorySection[];
};

const CHANGE_TYPE_ACTION: Record<string, string> = {
  CREATE: 'Added',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  CLOSE_AND_CREATE: 'Ended',
};

// field diffs compared between the prev*/new* pairs the API returns per history row
const HISTORY_FIELD_DEFS: { prevKey: string; newKey: string; label: string; isDate?: boolean }[] = [
  { prevKey: 'prevPayerPlanName', newKey: 'newPayerPlanName', label: 'Payer plan name' },
  { prevKey: 'prevStartDate', newKey: 'newStartDate', label: 'Start date', isDate: true },
  { prevKey: 'prevEndDate', newKey: 'newEndDate', label: 'End date', isDate: true },
  { prevKey: 'prevProgramType', newKey: 'newProgramType', label: 'Program type' },
  { prevKey: 'prevPriorMedicareADays', newKey: 'newPriorMedicareADays', label: 'Prior Med A days' },
];

const formatHistoryFieldValue = (value: any, isDate?: boolean): string => {
  if (value === null || value === undefined || value === '') return isDate ? 'Open' : '—';
  return isDate ? moment(value).format('MMM D, YYYY') : String(value);
};

// "Jan 12, 2026 – Mar 26, 2026" (closed) or "Sep 30, 2026 – open" (still active)
const formatDateRange = (start: any, end: any): string =>
  `${moment(start).format('MMM D, YYYY')} – ${end ? moment(end).format('MMM D, YYYY') : 'open'}`;

// The field-diff / range rows for one raw row.
// `skipLabels` leaves out fields already represented elsewhere — CLOSE_AND_CREATE's prev/new
// payer plan names are shown in the header line itself, so repeating them as a diff row
// ("Payer plan name: X → Y") would be redundant.
const buildRowsForRaw = (raw: any, skipLabels: string[] = []): PayerHistoryRow[] => {
  if (raw?.changeType === 'DELETE') {
    // Nothing to diff against — just show the date range the payer was active for.
    return [
      { label: 'Was', to: formatDateRange(raw?.prevStartDate, raw?.prevEndDate), isAdded: true },
    ];
  }
  if (raw?.changeType === 'CREATE') {
    // A brand-new payer has no "prev" to diff against either — one range row, not four
    // separate "— → X" rows for plan name/start date/program type/prior days.
    // Already-closed at creation (a backfilled historical payer) reads "Dates"; still
    // active reads "Added", matching how open vs. closed ranges are labeled everywhere else.
    return [
      {
        label: raw?.newEndDate ? 'Dates' : 'Added',
        to: formatDateRange(raw?.newStartDate, raw?.newEndDate),
        isAdded: true,
      },
    ];
  }
  return HISTORY_FIELD_DEFS.reduce((acc: PayerHistoryRow[], def) => {
    if (skipLabels.includes(def.label)) return acc;
    const from = raw?.[def.prevKey];
    const to = raw?.[def.newKey];
    if (String(from ?? '') === String(to ?? '')) return acc;
    acc.push({
      label: def.label,
      from: formatHistoryFieldValue(from, def.isDate),
      to: formatHistoryFieldValue(to, def.isDate),
    });
    return acc;
  }, []);
};

// "Entered N days after end/start date" — computed locally from enteredAt vs. whichever date
// the row actually set, since the API doesn't send this note itself.
const buildEnteredNote = (raw: any): string | undefined => {
  let target: { date: any; label: string } | null = null;
  if (raw?.changeType === 'CLOSE_AND_CREATE' && raw?.newEndDate) {
    target = { date: raw.newEndDate, label: 'end date' };
  } else if (raw?.changeType === 'CREATE') {
    target = raw?.newEndDate
      ? { date: raw.newEndDate, label: 'end date' }
      : raw?.newStartDate
      ? { date: raw.newStartDate, label: 'start date' }
      : null;
  } else if (raw?.changeType === 'UPDATE') {
    if (String(raw?.prevEndDate ?? '') !== String(raw?.newEndDate ?? '') && raw?.newEndDate) {
      target = { date: raw.newEndDate, label: 'end date' };
    } else if (
      String(raw?.prevStartDate ?? '') !== String(raw?.newStartDate ?? '') &&
      raw?.newStartDate
    ) {
      target = { date: raw.newStartDate, label: 'start date' };
    }
  }
  if (!target || !raw?.enteredAt) return undefined;
  const days = moment(raw.enteredAt)
    .startOf('day')
    .diff(moment(target.date).startOf('day'), 'days');
  if (days <= 0) return undefined;
  return `Entered ${days} day${days === 1 ? '' : 's'} after ${target.label}`;
};

// chip (if a payerType was resolved for this name) + the name itself, as description segments.
const namePartsFor = (name: string, payerType?: string): DescriptionPart[] => {
  const parts: DescriptionPart[] = [];
  if (payerType)
    parts.push({ type: 'chip', chip: { label: payerType.replaceAll('_', ' '), payerType } });
  parts.push({ type: 'text', value: name });
  return parts;
};

const authorFor = (raw: any): { author: string; isImported: boolean } => {
  const isImported = !raw?.enteredByFirstName && !raw?.enteredByLastName;
  return {
    isImported,
    author: isImported
      ? 'Imported'
      : `${raw?.enteredByFirstName ?? ''} ${raw?.enteredByLastName ?? ''}`.trim(),
  };
};

// Maps one raw /patient-payers/history/patient row into the shape the drawer renders.
// `payerTypeByName` is a best-effort lookup (built from the payer-options dropdown's own
// payerType field) — the history rows themselves don't carry a payerType, only a plan name.
//
// CLOSE_AND_CREATE is self-contained: prevPayerPlanName is the payer that ended,
// newPayerPlanName is the payer that was added — both on this one row, no partner row needed.
const mapPayerHistoryEntry = (
  raw: any,
  payerTypeByName: Record<string, string> = {}
): PayerHistoryEntry => {
  const isDeleted = raw?.changeType === 'DELETE';
  const isCloseAndCreate = raw?.changeType === 'CLOSE_AND_CREATE';
  const action = CHANGE_TYPE_ACTION[raw?.changeType] ?? raw?.changeType ?? '';
  const { author, isImported } = authorFor(raw);

  let descriptionParts: DescriptionPart[];
  let rows: PayerHistoryRow[];

  if (isCloseAndCreate) {
    const endedName = raw?.prevPayerPlanName ?? '';
    const addedName = raw?.newPayerPlanName ?? '';
    descriptionParts = [
      ...namePartsFor(endedName, payerTypeByName[endedName]),
      { type: 'text', value: ' and added ' },
      ...namePartsFor(addedName, payerTypeByName[addedName]),
    ];
    // "Payer plan name" is already said above — showing it again as "X → Y" would be redundant.
    rows = buildRowsForRaw(raw, ['Payer plan name']);
  } else {
    const payerName = raw?.newPayerPlanName ?? raw?.prevPayerPlanName ?? '';
    const startDate = raw?.newStartDate ?? raw?.prevStartDate;
    descriptionParts = namePartsFor(payerName, payerTypeByName[payerName]);
    // A DELETE row has no "new" values — the summary just names the payer, not when it started.
    if (!isDeleted && startDate) {
      descriptionParts.push({
        type: 'text',
        value: ` starting ${moment(startDate).format('MMM D, YYYY')}`,
      });
    }
    rows = buildRowsForRaw(raw);
  }

  return {
    date: raw?.enteredAt ? moment(raw.enteredAt).format('MMM D, YYYY') : '—',
    time: raw?.enteredAt ? moment(raw.enteredAt).format('h:mm A') : '',
    author,
    isImported,
    action,
    isDeleted,
    descriptionParts,
    note: buildEnteredNote(raw),
    sections: [{ rows }],
  };
};

// "MEDICARE_A" -> "Medicare A"
const humanizePayerType = (payerType?: string): string =>
  (payerType ?? '')
    .split('_')
    .filter(Boolean)
    .map((word) =>
      word.length <= 1 ? word.toUpperCase() : word[0].toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');

const PayerHistoryChipLabel = ({ chip }: { chip: PayerHistoryChip }) => {
  const tone = getPayerCateGroup(chip?.payerType);
  return (
    <Chip
      size="small"
      label={chip.label}
      sx={{
        fontFamily: 'Inter ',
        fontSize: '10px ',
        fontWeight: 500,
        width: '125px',
        height: '22px',
        color: '#111927',
        textOverflow: 'ellipsis',
        borderRadius: '20px',
        borderBottom: `2px solid ${tone?.borderColor}`,
        background: tone?.bgColor,
      }}
    />
  );
};

export const PayerHistoryDrawer = ({
  open,
  onClose,
  patientUuid,
  initialPatientPayerUuid,
}: {
  open: boolean;
  onClose: () => void;
  patientUuid?: string;
  initialPatientPayerUuid?: string;
}) => {
  const dispatch = useDispatch();
  // Lazy-initialized once from the prop at mount — the parent remounts this component (via a
  // changing `key`) whenever a new History request is opened, so this never needs to re-sync
  // mid-lifecycle and can't race with the fetch effect below.
  const [payerFilter, setPayerFilter] = useState(initialPatientPayerUuid ?? 'all');
  const payerOptionsResponse = useSelector((state: any) => state?.getPayerOptionsReducer?.response);
  const payerOptions = payerOptionsResponse?.data ?? [];

  const PAGE_SIZE = 20;
  const [page, setPage] = useState(0);
  // The API only ever returns one page's worth of content — pages beyond the first are
  // appended here rather than replacing what's already been shown.
  const [accumulatedRows, setAccumulatedRows] = useState<any[]>([]);

  const payerHistoryReducer = useSelector((state: any) => state?.getPayerHistoryReducer);
  const payerHistoryLoading = !!payerHistoryReducer?.loading;
  const payerHistoryResponseData = payerHistoryReducer?.response?.data;
  const hasMoreHistory = payerHistoryResponseData ? !payerHistoryResponseData?.last : false;
  const payerTypeByName: Record<string, string> = payerOptions.reduce(
    (acc: Record<string, string>, payer: any) => {
      if (payer?.payerPlanName) acc[payer.payerPlanName] = payer.payerType;
      return acc;
    },
    {}
  );
  const historyEntries = accumulatedRows.map((row: any) => mapPayerHistoryEntry(row, payerTypeByName));

  useEffect(() => {
    if (!open || !patientUuid) return;
    dispatch(getPayerOptionsAction({ patientUuid }));
  }, [open, patientUuid]);

  // Switching payers resets back to the first page — a page offset from the old filter's
  // result set has no meaning against the new one.
  useEffect(() => {
    setPage(0);
  }, [payerFilter]);

  useEffect(() => {
    if (!open || !patientUuid) return;
    dispatch(
      getPayerHistoryAction({
        patientUuid,
        patientPayerUuid: payerFilter !== 'all' ? payerFilter : '',
        page,
        pageSize: PAGE_SIZE,
      })
    );
  }, [open, patientUuid, payerFilter, page]);

  useEffect(() => {
    if (!payerHistoryResponseData) return;
    setAccumulatedRows((prev) =>
      page === 0 ? payerHistoryResponseData?.content ?? [] : [...prev, ...(payerHistoryResponseData?.content ?? [])]
    );
  }, [payerHistoryResponseData]);

  const handleShowOlderChanges = () => {
    if (payerHistoryLoading || !hasMoreHistory) return;
    setPage((prev) => prev + 1);
  };

  useEffect(() => {
    return () => {
      dispatch(resetGetPayerOptionsAction());
      dispatch(resetGetPayerHistoryAction());
    };
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: '35vw', p: 3 } }}
    >
      <Stack
        flexDirection="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h6">Payer history</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Who changed this patient&apos;s payers, when, and what changed.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Select
        size="small"
        value={payerFilter}
        onChange={(e) => setPayerFilter(e.target.value)}
        sx={{ mt: 2.5 }}
        MenuProps={{ MenuListProps: { sx: { '& .MuiMenuItem-root': { fontSize: '14px' } } } }}
      >
        <MenuItem value="all">All payers</MenuItem>
        {payerOptions.map((payer: any) => (
          <MenuItem
            key={payer?.patientPayerUuid}
            value={payer?.patientPayerUuid}
          >
            {humanizePayerType(payer?.payerType)} · {payer?.payerPlanName} · from{' '}
            {payer?.startDate ? moment(payer.startDate).format('MMM D, YYYY') : '—'}
          </MenuItem>
        ))}
      </Select>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'right', mt: 1 }}
      >
        Times in AKDT (facility time)
      </Typography>

      <Stack sx={{ mt: 1.5 }}>
        {payerHistoryLoading && accumulatedRows.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2 }}
          >
            Loading…
          </Typography>
        )}
        {!payerHistoryLoading && historyEntries.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2 }}
          >
            No payer history found.
          </Typography>
        )}
        {historyEntries.length > 0 &&
          historyEntries.map((entry: PayerHistoryEntry, index: number) => (
            <Box key={index}>
              <Box sx={{ py: 2 }}>
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  spacing={0.75}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {entry.date} · {entry.time} ·{' '}
                  </Typography>
                  {entry.isImported ? (
                    <Chip
                      size="small"
                      label={entry.author}
                      variant="outlined"
                      sx={{
                        // height: '18px',
                        fontSize: '10px',
                        fontWeight: 600,
                        color: 'text.secondary',
                        borderColor: '#E5E7EB',
                      }}
                    />
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: '0px !important' }}
                    >
                      {entry.author}
                    </Typography>
                  )}
                </Stack>

                <Typography
                  sx={{
                    fontSize: '14px',
                    mt: 0.5,
                    color: entry.isDeleted ? '#B42318' : undefined,
                    fontWeight: 600,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontWeight: 600 }}
                  >
                    {entry.action}{' '}
                  </Box>
                  {entry.descriptionParts.map((part, partIndex) =>
                    part.type === 'chip' ? (
                      <Box
                        key={partIndex}
                        component="span"
                        sx={{ display: 'inline-flex', verticalAlign: 'middle', mr: 0.5 }}
                      >
                        <PayerHistoryChipLabel chip={part.chip} />
                      </Box>
                    ) : (
                      <Box
                        key={partIndex}
                        component="span"
                      >
                        {part.value}
                      </Box>
                    )
                  )}
                </Typography>

                {entry.note && (
                  <Chip
                    size="small"
                    label={entry.note}
                    sx={{
                      borderRadius: '8px',
                      mt: 1,
                      height: '22px',
                      fontSize: '11px',
                      color: '#8E4B10',
                      bgcolor: '#FDF6B2',
                      border: '1px solid #e19553',
                    }}
                  />
                )}

                <Stack
                  spacing={1}
                  sx={{ mt: entry.note ? 1 : 0.75 }}
                >
                  {entry.sections.map((section, sectionIndex) => (
                    <Box key={sectionIndex}>
                      {section.heading && (
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 0.5 }}>
                          {section.heading}
                        </Typography>
                      )}
                      {section.rows.map((row, rowIndex) => (
                        <Stack
                          key={rowIndex}
                          flexDirection="row"
                          alignItems="center"
                          spacing={2}
                        >
                          <Typography
                            color="text.secondary"
                            sx={{ fontSize: '13px', minWidth: '120px' }}
                          >
                            {row.label}
                          </Typography>
                          <Typography
                            color="text.primary"
                            sx={{ fontSize: '13px', mt: '0px !important' }}
                          >
                            {row.isAdded ? (
                              row.to
                            ) : (
                              <>
                                <Box
                                  component="span"
                                  sx={{ color: 'text.disabled', textDecoration: 'line-through' }}
                                >
                                  {row.from}
                                </Box>{' '}
                                → {row.to}
                              </>
                            )}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>
                  ))}
                </Stack>
              </Box>
              {index < historyEntries.length - 1 && <Divider />}
            </Box>
          ))}
        {hasMoreHistory && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleShowOlderChanges}
              disabled={payerHistoryLoading}
              startIcon={payerHistoryLoading ? <CircularProgress size={14} /> : undefined}
            >
              {payerHistoryLoading ? 'Loading…' : 'Show older changes'}
            </Button>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
};
